import React from 'react'
import * as RC from 'recharts'
import { toCssVar } from '../../../theme/foundations/colors'

const mapColorProps = <T extends Record<string, any>>(props: T, keys: string[]): T => {
  const out: any = { ...props }
  for (const k of keys) if (k in out) out[k] = toCssVar(out[k])
  return out
}

const mapTick = (tick: any) =>
  typeof tick === 'object' && tick !== null ? mapColorProps(tick, ['fill', 'stroke']) : tick

const mapDot = (dot: any) => {
  if (dot === true || dot === false || dot == null) return dot
  if (typeof dot === 'object') return mapColorProps(dot, ['fill', 'stroke'])
  return dot
}

const withColorMap = <P extends Record<string, any>>(
  Comp: React.ComponentType<P>,
  colorKeys: string[] = [],
  options?: { tick?: boolean; dot?: boolean; axis?: boolean }
) =>
  React.forwardRef<any, P>(function Wrapped(p, ref) {
    let props: any = mapColorProps(p, colorKeys)

    if (options?.tick && 'tick' in props) props.tick = mapTick(props.tick)
    if (options?.dot) {
      if ('dot' in props) props.dot = mapDot(props.dot)
      if ('activeDot' in props) props.activeDot = mapDot(props.activeDot)
    }
    if (options?.axis) {
      if ('axisLine' in props && typeof props.axisLine === 'object')
        props.axisLine = mapColorProps(props.axisLine, ['stroke'])
      if ('tickLine' in props && typeof props.tickLine === 'object')
        props.tickLine = mapColorProps(props.tickLine, ['stroke'])
    }

    return <Comp ref={ref as any} {...props} />
  })

/* ---------- Wrapped Recharts primitives (auto token → var) ---------- */

// charts/containers (no color props – pass through)
export const ResponsiveContainer = RC.ResponsiveContainer
export const LineChart = RC.LineChart
export const AreaChart = RC.AreaChart
export const BarChart = RC.BarChart
export const ComposedChart = RC.ComposedChart
export const PieChart = RC.PieChart
export const RadarChart = RC.RadarChart
export const RadialBarChart = RC.RadialBarChart
export const ScatterChart = RC.ScatterChart

// axes/grid
export const CartesianGrid = withColorMap(RC.CartesianGrid, ['stroke'])
export const XAxis = withColorMap(RC.XAxis, ['stroke'], {
  tick: true,
  axis: true,
})
export const YAxis = withColorMap(RC.YAxis, ['stroke'], {
  tick: true,
  axis: true,
})
export const ZAxis = withColorMap(RC.ZAxis, ['stroke'], { tick: true })

// series
export const Line = withColorMap(RC.Line, ['stroke', 'fill'], { dot: true })
export const Area = withColorMap(RC.Area, ['stroke', 'fill'], { dot: true })
export const Bar = withColorMap(RC.Bar, ['stroke', 'fill'])
export const Pie = withColorMap(RC.Pie, ['stroke', 'fill'])
export const Radar = withColorMap(RC.Radar, ['stroke', 'fill'])
export const RadialBar = withColorMap(RC.RadialBar, ['stroke', 'fill'])
export const Scatter = withColorMap(RC.Scatter, ['stroke', 'fill'])

// annotations & misc
// export const ReferenceLine = withColorMap(RC.ReferenceLine, ["stroke"]);
// export const ReferenceArea = withColorMap(RC.ReferenceArea, ["stroke", "fill"]);
export const ReferenceDot = withColorMap(RC.ReferenceDot, ['stroke', 'fill'])
export const Brush = withColorMap(RC.Brush, ['stroke', 'fill'])
export const ErrorBar = withColorMap(RC.ErrorBar, ['stroke', 'fill'])
export const Label = withColorMap(RC.Label, ['fill', 'stroke'])
export const LabelList = withColorMap(RC.LabelList, ['fill', 'stroke'])
// export const Legend = withColorMap(RC.Legend, []); // use wrapperStyle manually if needed

// tooltip (usually custom content handles Chakra)
export const Tooltip = RC.Tooltip

// helpers re-export
export const Rectangle = withColorMap(RC.Rectangle, ['fill', 'stroke'])
export const Sector = withColorMap(RC.Sector, ['fill', 'stroke'])
export const Dot = withColorMap(RC.Dot, ['fill', 'stroke'])
// export const Legend = withColorMap(RC.Legend, ["fill", "stroke"]);

// everything else that isn't wrapped can still be accessed via RC if needed
export * as RechartsRaw from 'recharts'
